/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";

export const SET_COLLECT_PROTOCOL_FEES_AUTHORITY_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([34, 150, 93, 244, 139, 225, 233, 67]);

export function getSetCollectProtocolFeesAuthorityDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SET_COLLECT_PROTOCOL_FEES_AUTHORITY_DISCRIMINATOR,
  );
}

export type SetCollectProtocolFeesAuthorityInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountWhirlpoolsConfig extends string | AccountMeta = string,
  TAccountCollectProtocolFeesAuthority extends string | AccountMeta = string,
  TAccountNewCollectProtocolFeesAuthority extends string | AccountMeta = string,
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountWhirlpoolsConfig extends string
        ? WritableAccount<TAccountWhirlpoolsConfig>
        : TAccountWhirlpoolsConfig,
      TAccountCollectProtocolFeesAuthority extends string
        ? ReadonlySignerAccount<TAccountCollectProtocolFeesAuthority> &
            AccountSignerMeta<TAccountCollectProtocolFeesAuthority>
        : TAccountCollectProtocolFeesAuthority,
      TAccountNewCollectProtocolFeesAuthority extends string
        ? ReadonlyAccount<TAccountNewCollectProtocolFeesAuthority>
        : TAccountNewCollectProtocolFeesAuthority,
      ...TRemainingAccounts,
    ]
  >;

export interface SetCollectProtocolFeesAuthorityInstructionData {
  discriminator: ReadonlyUint8Array;
}

export type SetCollectProtocolFeesAuthorityInstructionDataArgs = {};

export function getSetCollectProtocolFeesAuthorityInstructionDataEncoder(): FixedSizeEncoder<SetCollectProtocolFeesAuthorityInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: SET_COLLECT_PROTOCOL_FEES_AUTHORITY_DISCRIMINATOR,
    }),
  );
}

export function getSetCollectProtocolFeesAuthorityInstructionDataDecoder(): FixedSizeDecoder<SetCollectProtocolFeesAuthorityInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getSetCollectProtocolFeesAuthorityInstructionDataCodec(): FixedSizeCodec<
  SetCollectProtocolFeesAuthorityInstructionDataArgs,
  SetCollectProtocolFeesAuthorityInstructionData
> {
  return combineCodec(
    getSetCollectProtocolFeesAuthorityInstructionDataEncoder(),
    getSetCollectProtocolFeesAuthorityInstructionDataDecoder(),
  );
}

export interface SetCollectProtocolFeesAuthorityInput<
  TAccountWhirlpoolsConfig extends string = string,
  TAccountCollectProtocolFeesAuthority extends string = string,
  TAccountNewCollectProtocolFeesAuthority extends string = string,
> {
  whirlpoolsConfig: Address<TAccountWhirlpoolsConfig>;
  collectProtocolFeesAuthority: TransactionSigner<TAccountCollectProtocolFeesAuthority>;
  newCollectProtocolFeesAuthority: Address<TAccountNewCollectProtocolFeesAuthority>;
}

export function getSetCollectProtocolFeesAuthorityInstruction<
  TAccountWhirlpoolsConfig extends string,
  TAccountCollectProtocolFeesAuthority extends string,
  TAccountNewCollectProtocolFeesAuthority extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: SetCollectProtocolFeesAuthorityInput<
    TAccountWhirlpoolsConfig,
    TAccountCollectProtocolFeesAuthority,
    TAccountNewCollectProtocolFeesAuthority
  >,
  config?: { programAddress?: TProgramAddress },
): SetCollectProtocolFeesAuthorityInstruction<
  TProgramAddress,
  TAccountWhirlpoolsConfig,
  TAccountCollectProtocolFeesAuthority,
  TAccountNewCollectProtocolFeesAuthority
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    whirlpoolsConfig: {
      value: input.whirlpoolsConfig ?? null,
      isWritable: true,
    },
    collectProtocolFeesAuthority: {
      value: input.collectProtocolFeesAuthority ?? null,
      isWritable: false,
    },
    newCollectProtocolFeesAuthority: {
      value: input.newCollectProtocolFeesAuthority ?? null,
      isWritable: false,
    },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("whirlpoolsConfig", accounts.whirlpoolsConfig),
      getAccountMeta(
        "collectProtocolFeesAuthority",
        accounts.collectProtocolFeesAuthority,
      ),
      getAccountMeta(
        "newCollectProtocolFeesAuthority",
        accounts.newCollectProtocolFeesAuthority,
      ),
    ],
    data: getSetCollectProtocolFeesAuthorityInstructionDataEncoder().encode({}),
    programAddress,
  } as SetCollectProtocolFeesAuthorityInstruction<
    TProgramAddress,
    TAccountWhirlpoolsConfig,
    TAccountCollectProtocolFeesAuthority,
    TAccountNewCollectProtocolFeesAuthority
  >);
}

export interface ParsedSetCollectProtocolFeesAuthorityInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    whirlpoolsConfig: TAccountMetas[0];
    collectProtocolFeesAuthority: TAccountMetas[1];
    newCollectProtocolFeesAuthority: TAccountMetas[2];
  };
  data: SetCollectProtocolFeesAuthorityInstructionData;
}

export function parseSetCollectProtocolFeesAuthorityInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSetCollectProtocolFeesAuthorityInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 3,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      whirlpoolsConfig: getNextAccount(),
      collectProtocolFeesAuthority: getNextAccount(),
      newCollectProtocolFeesAuthority: getNextAccount(),
    },
    data: getSetCollectProtocolFeesAuthorityInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
